import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Input,
  Modal,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomToolTip from "../../../../components/CustomTooltip";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import SpinningIcon from "../../../../components/SpinningIcon";
import { Toast } from "../../../../components/Toast";
import { NotificationContext } from "../../../../contexts/NotificationContext";

const ScanSpecificControlButton = ({
  loadingMessage,
  setLoading,
  ...props
}) => {
  const { isLoading, setIsLoading, setIsScanning, setTaskId } =
    useContext(NotificationContext);
  const colors = useColors();
  const { requestPartialScan, trackEvent } = useAPI();
  const [openModal, setOpenModal] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [controlId, setControlId] = useState("");
  const [buildRelatedData, setBuildRelatedData] = useState(true);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  const handleButtonClick = () => {
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setControlId("");
    setBuildRelatedData(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleScanClick = () => {
    if (!controlId || !controlId.trim()) {
      Toast.error("Please enter a control ID");
      return;
    }
    setOpenDialog(true);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    const message = `User run "SCAN" for specific control: ${controlId}`;
    trackEvent(message).then();

    requestPartialScan(controlId.trim(), buildRelatedData)
      .then((result) => {
        setIsScanning(true);
        Toast.info("Scanning control in the background...");
        setTaskId(result.data);
        handleModalClose();
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(error.response.data.detail || "Failed to scan control");
        } else {
          Toast.error("Failed to scan control");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    setOpenDialog(false);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: colors.common_white,
    borderRadius: "12px",
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
      <CustomToolTip title={isLoading ? loadingMessage : ""}>
        <Button
          sx={{
            background: colors.grey_50,
            color: colors.contrast_text_dark,
            height: "32px",
            marginLeft: "5px",
            border: "1px solid",
            borderRadius: "8px",
          }}
          startIcon={<SpinningIcon isLoading={isLoading} />}
          onClick={handleButtonClick}
          disabled={isLoading}
          {...props}
        >
          {isLoading ? "Scanning..." : "Scan Specific Control"}
        </Button>
      </CustomToolTip>

      <Modal
        open={openModal}
        onClose={handleModalClose}
        aria-labelledby="scan-control-modal"
      >
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            Scan Specific Control
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 2, color: colors.text_secondary }}
          >
            Enter the control ID to scan. This is useful when a control does not
            exist yet.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
              Control ID
            </Typography>
            <Input
              fullWidth
              placeholder="e.g., 123"
              value={controlId}
              onChange={(e) => setControlId(e.target.value)}
              type="number"
              sx={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: `1px solid ${colors.grey_32percent}`,
                "&:before, &:after": {
                  display: "none",
                },
              }}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={buildRelatedData}
                onChange={(event) => setBuildRelatedData(event.target.checked)}
              />
            }
            label="Build related data (heatmaps, widgets, MITRE, etc.)"
            sx={{ color: colors.text_secondary, mb: 1 }}
          />

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <SecondaryButton onClick={handleModalClose}>Cancel</SecondaryButton>
            <PrimaryButton
              onClick={handleScanClick}
              disabled={!controlId || !controlId.trim()}
            >
              Scan
            </PrimaryButton>
          </Box>
        </Box>
      </Modal>

      <ConfirmationDialog
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        handleConfirm={handleConfirm}
        dialogContentFirstLine={`Are you sure you want to scan control ${controlId}?`}
        dialogContentSecondLine="This action can take a few minutes."
        dialogContentThirdLine={
          buildRelatedData
            ? "Related data will be rebuilt."
            : "Related data build will be skipped."
        }
        confirmationButtonText="Scan"
      />
    </>
  );
};

export default ScanSpecificControlButton;
